import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { useAppState } from '../AppStateProvider';
import { EmptyStateView } from '../components/EmptyStateView';
import { useCompanyEmptyState } from '../components/EmptyStateView/useCompanyEmptyState';
import { Header } from '../components/Header';
import type { Tab } from '../components/Tabs';
import { TabsView } from '../components/Tabs';
import { CompanyTabName, Page } from '../domain/page';
import { useTopCountriesQuery } from '../features/company/useTopCountriesQuery';
import { useInsightsQuery } from '../features/insights/useInsightsQuery';
import { useDisabledPageView } from '../hooks/useDisabledPageView';
import { useFilterCollectedDomains } from '../hooks/useFilterCollectedDomains';
import { AppcuesService } from '../services/appcues';
import { appStore } from '../services/appStore';
import getTrackingLocation from '../services/tracking/getTrackingLocation';
import { useDispatchTrackEventWithLocation } from '../services/tracking/hooks/useDispatchTrackEventWithLocation';
import UrlService from '../services/UrlService';

import { CompanyOverviewContainer } from './company-overview/CompanyOverviewContainer';
import { MultipleDomainsContainer } from './company-overview/MultipleDomainsContainer';
import { useIsMultipleDomainsPage } from './company-overview/useIsMultipleDomainsPage';
import { ContactOverview } from './ContactOverview';
import { Contacts } from './contacts';
import { InsightsContainer } from './insights';
import { StyledTabsContainer } from './styles';

const OverviewTabContent = ({ onScrollToTop }: { onScrollToTop: () => void }) => {
  const { page } = useAppState();
  const isMultipleDomainsPage = useIsMultipleDomainsPage();
  const OverviewComponent = page === Page.Contact ? ContactOverview : CompanyOverviewContainer;

  return (
    <>
      {isMultipleDomainsPage ? <MultipleDomainsContainer /> : null}
      <OverviewComponent onScrollToTop={onScrollToTop} />
    </>
  );
};

export const CompanyPage = () => {
  const parentRef = useRef<HTMLDivElement>(null);

  const onScrollToTop = useCallback(() => {
    if (parentRef.current) {
      parentRef.current.scrollTop = 0;
    }
  }, []);

  const { t } = useTranslation();
  const { page, contactLinkedinId, companyLinkedinId, companyTabName: activeTabName, url } = useAppState();
  const dispatchTrackEventWithLocation = useDispatchTrackEventWithLocation();

  const { isDisabledPageVisible, disabledPageView } = useDisabledPageView();

  const filteredDomains = useFilterCollectedDomains();
  useTopCountriesQuery();
  useInsightsQuery();

  const { isEmptyState, emptyStateProps } = useCompanyEmptyState();

  const OverviewTab = useCallback(() => <OverviewTabContent onScrollToTop={onScrollToTop} />, [onScrollToTop]);

  const tabs: Tab<CompanyTabName>[] = useMemo(
    () => [
      {
        name: CompanyTabName.Overview,
        label: t('company.overview'),
        iconProps: { iconName: 'search', width: 28 },
        component: OverviewTab,
      },
      {
        name: CompanyTabName.Insights,
        label: t('company.insights'),
        iconProps: { iconName: 'sparkles-ai', colorMethod: 'fill' },
        component: InsightsContainer,
      },
      {
        name: CompanyTabName.Contacts,
        label: t('company.contacts'),
        iconProps: { iconName: 'contacts', colorMethod: 'stroke', width: 20 },
        component: Contacts,
      },
    ],
    [t, OverviewTab]
  );

  useEffect(() => {
    appStore.setCompanyTabName(CompanyTabName.Overview);
    appStore.resetEmailGeneration();
  }, [contactLinkedinId, companyLinkedinId]);

  useEffect(() => {
    if (url && UrlService.isCalendarUrl(new URL(url))) {
      if (filteredDomains.length !== 0 && page === Page.Meeting) {
        appStore.setCompanyDomain(filteredDomains[0]);
        appStore.setCompanyTabName(CompanyTabName.Overview);
      } else {
        appStore.setCompanyDomain(UrlService.removeWWW(new URL(url).hostname) || '');
      }
    }
  }, [filteredDomains, page, url]);

  const handleSelectTab = (newTab: CompanyTabName) => {
    const location = getTrackingLocation(url);
    dispatchTrackEventWithLocation('navigation', 'select tab', `${newTab}/${activeTabName}/see on ${location}`);
    appStore.setCompanyTabName(newTab);
    AppcuesService.pageChange(newTab === CompanyTabName.Overview ? page : `${page}-${newTab}`);
  };

  if (isDisabledPageVisible) {
    return disabledPageView;
  }

  return (
    <>
      <Header />
      {isEmptyState ? (
        <EmptyStateView {...emptyStateProps} />
      ) : (
        <StyledTabsContainer ref={parentRef}>
          <TabsView<CompanyTabName>
            tabs={tabs}
            onSelect={handleSelectTab}
            activeTabName={activeTabName}
            tabsPosition="top"
            variant="sticky"
          />
        </StyledTabsContainer>
      )}
    </>
  );
};
